<template>
  <div ref="host" class="supply-demand-card-host">
    <!-- 供需信息：仅作为无障碍名称，不再占据可见标题空间 -->
    <article ref="card" class="supply-demand-goods-card"
      :class="{ 'is-expanded': expanded, 'is-animating': animating, 'is-mobile': mobile }"
      :style="cardStyle" :aria-label="$t('agentServiceUI.supplyDemandScene')" :aria-describedby="`supply-card-hint-${_uid}`"
      @dblclick.stop.prevent="toggle">
      <div ref="viewport" class="card-viewport">
        <div ref="body" class="card-body" :style="bodyStyle">
          <!-- 货物资料暂不可用，请重新打开此信息 -->
          <p v-if="!validCard" class="card-unavailable">{{ $t('agentServiceUI.supplyDemandCardUnavailable') }}</p>
          <template v-else>
            <dl ref="summary" class="card-summary">
              <!-- 动态简中：产品品牌、产品料号、产品类型、容量、速率；仅显示快照，不猜补业务值 -->
              <div v-for="field in summaryFields" :key="field.key" class="card-field">
                <dt>{{ $t('agentServiceUI.supplyDemandField.' + field.key) }}</dt>
                <dd dir="auto">{{ field.value }}</dd>
              </div>
            </dl>
            <dl :id="`supply-card-details-${_uid}`" class="card-details" :aria-hidden="!expanded"
              :inert="!expanded ? '' : null">
              <!-- 动态简中：沿用 supplyDemandField 中信息编号、数量、价格、备注等已有三语字段 -->
              <div v-for="field in detailFields" :key="field.key" class="card-field">
                <dt>{{ $t('agentServiceUI.supplyDemandField.' + field.key) }}</dt>
                <dd dir="auto">{{ field.value }}</dd>
              </div>
            </dl>
          </template>
        </div>
      </div>
      <footer class="card-footer">
        <!-- 双击卡片可展开或收起货物资料 -->
        <span :id="`supply-card-hint-${_uid}`" class="sr-only">{{ $t('agentServiceUI.supplyDemandCardToggleHint') }}</span>
        <!-- 展开货物资料 / 收起货物资料 -->
        <button type="button" class="card-toggle" :disabled="!validCard"
          :aria-expanded="expanded" :aria-controls="`supply-card-details-${_uid}`"
          :aria-label="$t(expanded ? 'agentServiceUI.supplyDemandCardCollapse' : 'agentServiceUI.supplyDemandCardExpand')"
          :title="$t(expanded ? 'agentServiceUI.supplyDemandCardCollapse' : 'agentServiceUI.supplyDemandCardExpand')"
          @click.stop="toggleFromButton" @dblclick.stop.prevent>
          <svg viewBox="0 0 24 24" aria-hidden="true" focusable="false">
            <circle class="toggle-ring" cx="12" cy="12" r="9" />
            <path class="toggle-arrow" d="M7.5 7.5l9 9M10.5 16.5h6v-6" />
          </svg>
        </button>
      </footer>
    </article>
  </div>
</template>

<script>
const SUMMARY_KEYS = ['goodsBrand', 'partNumber', 'productType', 'capacity', 'speed'];
const COMPACT_CARD_WIDTH = 248;
const FACT_KEYS = ['infoNumber', 'clueType', 'partNumber', 'goodsName', 'goodsTitle', 'goodsBrand', 'goodsType',
  'goodsPrice', 'goodsPriceUnit', 'goodsQuantity', 'goodsUnit', 'manufactureDate', 'usageScenario',
  'deliveryAddress', 'goodsCategoryName', 'categoryAttributes', 'goodsImage', 'pictures', 'specificationUrl',
  'originCertificate', 'manufacturer', 'weight', 'length', 'width', 'height', 'goodsBuyMinQty', 'substitutes',
  'applications', 'goodsPeriod', 'deliveryPeriod', 'traderCompany', 'remark', 'productType', 'capacity',
  'architecture', 'speed', 'ball'];

/** 供需专用只读货物气泡；只管理显示与开合，不创建会话、不发请求、不改写业务快照。 */
export default {
  name: 'SupplyDemandGoodsCard',
  props: { message: { type: Object, required: true }, mobile: { type: Boolean, default: false } },
  data() {
    return { expanded: false, animating: false, dimensions: null, contentWidth: 0,
      motionEpoch: 0, motionFrame: null, settleTimer: null, resizeObserver: null, lastHostWidth: 0 };
  },
  computed: {
    snapshot() {
      try {
        const card = JSON.parse(this.message.content);
        if (!card || !card.contextId || !card.publisherUserId || !card.facts ||
          typeof card.facts !== 'object' || Array.isArray(card.facts)) return null;
        return card;
      } catch { return null; }
    },
    validCard() { return Boolean(this.snapshot); },
    /** 白名单展示纯文本；保留数量0、料号、单位代码和备注，不执行HTML或自动访问链接。 */
    fields() {
      if (!this.snapshot) return [];
      const facts = this.snapshot.facts;
      return FACT_KEYS.filter(key => ['string', 'number'].includes(typeof facts[key]) && String(facts[key]).trim())
        .map(key => {
          let value = facts[key];
          if (key === 'clueType') {
            // 供应 / 求购；未知业务码按原文保留，不冒充求购。
            if (Number(value) === 1) value = this.$t('agentServiceUI.supplyDemandSupply');
            else if (Number(value) === 2) value = this.$t('agentServiceUI.supplyDemandDemand');
          }
          if (key === 'goodsPriceUnit') value = Number(value) === 0 ? 'CNY' : Number(value) === 10 ? 'USD' : value;
          if (key === 'goodsPrice') value = `${Number(facts.goodsPriceUnit) === 0 ? 'CNY ' : Number(facts.goodsPriceUnit) === 10 ? 'USD ' : ''}${value}`;
          if (key === 'goodsQuantity' && facts.goodsUnit) value = `${value} ${facts.goodsUnit}`;
          return { key, value: String(value) };
        });
    },
    summaryFields() {
      return SUMMARY_KEYS.map(key => this.fields.find(field => field.key === key) || { key, value: '—' });
    },
    detailFields() { return this.fields.filter(field => !SUMMARY_KEYS.includes(field.key)); },
    uiLocale() { return this.$store?.state?.locale?.uiLocale || 'zh-CN'; },
    cardStyle() {
      return this.dimensions
        ? { width: `${this.dimensions.width}px`, height: `${this.dimensions.height}px` }
        : { width: `min(${COMPACT_CARD_WIDTH}px, 100%)`, height: '152px' };
    },
    bodyStyle() { return this.contentWidth ? { width: `${Math.max(0, this.contentWidth - 2)}px` } : {}; },
  },
  watch: {
    uiLocale() { this.$nextTick(this.resizeToContent); },
    'message.content'() {
      this.cancelMotion();
      this.expanded = false;
      this.$nextTick(this.resizeToContent);
    },
  },
  mounted() {
    this.resizeToContent();
    if (typeof ResizeObserver !== 'undefined') {
      this.resizeObserver = new ResizeObserver(entries => {
        const width = entries[0]?.contentRect.width;
        // 只响应可用宽度变化；卡片自身高度动画不能反过来打断动画。
        if (width > 0 && Math.abs(width - this.lastHostWidth) > 0.5) this.resizeToContent();
      });
      this.resizeObserver.observe(this.$refs.host);
    }
  },
  beforeDestroy() {
    this.cancelMotion();
    this.resizeObserver?.disconnect();
  },
  methods: {
    /** 双击箭头会产生两次click；仅处理第一下，避免开合抵消。键盘点击仍正常执行。 */
    toggleFromButton(event) { if (event.detail < 2) this.toggle(); },
    prefersReducedMotion() {
      return typeof window !== 'undefined' && Boolean(window.matchMedia?.('(prefers-reduced-motion: reduce)').matches);
    },
    scheduleFrame(fn) { return typeof window !== 'undefined' && window.requestAnimationFrame ? window.requestAnimationFrame(fn) : setTimeout(fn, 0); },
    cancelFrame(id) {
      if (typeof window !== 'undefined' && window.cancelAnimationFrame) window.cancelAnimationFrame(id);
      else clearTimeout(id);
    },
    /** 快速反向、切货物或销毁时立即废弃旧帧和定时器。 */
    cancelMotion() {
      this.motionEpoch++;
      if (this.motionFrame != null) this.cancelFrame(this.motionFrame);
      clearTimeout(this.settleTimer);
      this.motionFrame = null;
      this.settleTimer = null;
      this.animating = false;
    },
    availableWidth() { return Math.max(1, Math.floor(this.$refs.host?.clientWidth || COMPACT_CARD_WIDTH)); },
    /** 当前排版宽度下按全部资料测量展开高度；卡片不独立滚动，统一由聊天列表滚动。 */
    measureDimensions(width) {
      const summary = this.$refs.summary;
      // 摘要末尾仅留12px内边距和2px边框，不再为按钮预留整行44px空白。
      // 按钮圆心放在卡片边界上，详情也不再为按钮单独保留底栏。
      const compact = Math.ceil(summary ? summary.offsetTop + summary.offsetHeight + (this.mobile ? 18 : 14) : (this.$refs.body?.scrollHeight || 80) + 2);
      const height = this.expanded ? Math.max(compact, (this.$refs.body?.scrollHeight || compact) + 2) : compact;
      return { width, height };
    },
    /** 窗口缩放与语言变化只重排尺寸，保留展开状态及原始事实，不重查后台。 */
    resizeToContent() {
      this.cancelMotion();
      this.lastHostWidth = this.availableWidth();
      this.contentWidth = Math.min(this.expanded ? 520 : COMPACT_CARD_WIDTH, this.lastHostWidth);
      const epoch = this.motionEpoch;
      this.$nextTick(() => {
        if (epoch === this.motionEpoch) this.dimensions = this.measureDimensions(this.contentWidth);
      });
    },
    /** 左上角固定，用同一帧、时长、缓动同时改变width与height，右边与下边同步抵达。 */
    toggle() {
      if (!this.validCard) return;
      // 用户正在读资料，通知列表暂停末尾追随，避免向下展开时整张卡片被反向拉上去。
      this.$emit('interact');
      const current = this.$refs.card?.getBoundingClientRect();
      this.cancelMotion();
      const epoch = this.motionEpoch;
      if (current?.width > 0 && current?.height > 0) this.dimensions = { width: current.width, height: current.height };
      this.expanded = !this.expanded;
      this.contentWidth = Math.min(this.expanded ? 520 : COMPACT_CARD_WIDTH, this.availableWidth());
      this.$nextTick(() => {
        if (epoch !== this.motionEpoch) return;
        const target = this.measureDimensions(this.contentWidth);
        if (this.prefersReducedMotion()) { this.dimensions = target; return; }
        // 先落实当前可见边界，再在下一帧启动两轴动画；快速反向不会跳回上一轮起点。
        if (this.$refs.card) void this.$refs.card.offsetWidth;
        this.motionFrame = this.scheduleFrame(() => {
          if (epoch !== this.motionEpoch) return;
          this.motionFrame = null;
          this.animating = true;
          this.dimensions = target;
          this.settleTimer = setTimeout(() => {
            if (epoch === this.motionEpoch) { this.animating = false; this.settleTimer = null; }
          }, 260);
        });
      });
    },
  },
};
</script>

<style lang="less" scoped>
.supply-demand-card-host { width: 100%; min-width: 0; }
.supply-demand-goods-card {
  --card-corner-radius: 12px;
  // 45度弧线上到两条外边的距离为 r × (1 - 1/√2)，不是外接矩形的顶点。
  --card-corner-anchor: calc(var(--card-corner-radius) * 0.2928932188);
  position: relative;
  box-sizing: border-box;
  max-width: 100%;
  overflow: visible;
  border: 1px solid var(--m-border-default, #dde3ee);
  border-radius: var(--card-corner-radius);
  color: var(--m-text-primary, #334155);
  background: linear-gradient(145deg, var(--m-surface, #fff), var(--m-surface-muted, #f8fafc));
  box-shadow: 0 2px 8px rgba(15, 23, 42, .04), inset 0 1px 0 var(--m-surface, #fff);

  &.is-mobile {
    .card-field { display: flex; flex-direction: column; gap: 0; dt { white-space: nowrap; overflow-x: auto; } dd { min-width: 0; } }
    .card-body { padding: 12px 16px 16px 12px; }
  }

  &.is-animating {
    transition: width 240ms cubic-bezier(.2,.8,.2,1), height 240ms cubic-bezier(.2,.8,.2,1);
  }
  // 内容仍按圆角裁切，外置按钮不能被卡片根节点裁掉。
  // clip只负责圆角和动画裁切，不创建内部滚动容器，也不截留聊天区滚轮。
  .card-viewport { position: absolute; inset: 0; overflow: clip; border-radius: inherit; }
  .card-body { position: relative; box-sizing: border-box; padding: 12px; }
  dl { margin: 0; }
  .card-field {
    display: grid;
    grid-template-columns: 72px minmax(0, 1fr);
    gap: 8px;
    padding: 0;
    font-size: 15px;
    line-height: 22px;
    dt { color: var(--m-text-secondary, #667085); }
    dd { margin: 0; white-space: pre-wrap; overflow-wrap: anywhere; }
    & + .card-field { margin-top: 4px; }
  }
  .card-details {
    margin-top: 16px;
    padding-top: 12px;
    border-top: 1px solid var(--m-divider, #eef2f7);
    opacity: 0;
    visibility: hidden;
    transition: opacity 200ms ease, visibility 0s linear 240ms;
  }
  &.is-expanded .card-details { opacity: 1; visibility: visible; transition-delay: 0s; }
  .card-unavailable { margin: 0; font-size: 15px; line-height: 22px; }
  .card-footer {
    position: absolute;
    // 补偿1px边框，圆心跟随真实圆角弧线；开合时和右/下边界一起移动。
    bottom: calc(var(--card-corner-anchor) - 1px);
    right: calc(var(--card-corner-anchor) - 1px);
    transform: translate(50%, 50%);
    width: 44px;
    height: 44px;
    display: flex;
    justify-content: flex-end;
    background: transparent;
  }
  .card-toggle {
    width: 44px;
    height: 44px;
    padding: 10px;
    border: 0;
    border-radius: 50%;
    background: transparent;
    color: var(--m-text-secondary, #667085);
    cursor: pointer;
    svg { display: block; width: 24px; height: 24px; fill: none; stroke: currentColor; stroke-width: 1.4; stroke-linecap: round; stroke-linejoin: round; transition: transform 240ms cubic-bezier(.2,.8,.2,1), color 200ms ease; }
    // 同色1px圆环融入卡片轮廓，固定浅白底遮住交叉边线，避免像额外挂了一个按钮。
    .toggle-ring { fill: var(--m-surface-muted, #f8fafc); stroke: var(--m-border-default, #dde3ee); stroke-width: 1; transition: stroke 200ms ease; }
    .toggle-arrow { stroke-width: 1.2; stroke-opacity: .72; }
    &:hover { color: var(--m-brand-600, #246de0); background: transparent; }
    &:hover .toggle-ring, &:focus-visible .toggle-ring { stroke: currentColor; }
    &:hover .toggle-arrow, &:focus-visible .toggle-arrow { stroke-opacity: 1; }
    &:focus-visible { outline: 2px solid var(--m-brand-500, #4286f5); outline-offset: -4px; }
    &:disabled { cursor: default; opacity: .5; }
  }
  &.is-expanded .card-toggle svg { transform: rotate(180deg); }
  .sr-only { position: absolute; width: 1px; height: 1px; padding: 0; margin: -1px; overflow: hidden; clip-path: inset(50%); white-space: nowrap; border: 0; }
  @media (prefers-reduced-motion: reduce) {
    &.is-animating, .card-details, .card-toggle svg { transition: none; }
  }
}
</style>
